import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { disconnectWatch, fetchPatients, SortOrder } from '../../store/patientsSlice.ts';

import DashboardIcon from '@mui/icons-material/Dashboard';
import InfoIcon from '@mui/icons-material/Info';
import WatchOffIcon from '@mui/icons-material/WatchOff';
import { Alert, Box, Container, IconButton, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { BackendUser } from '../../store/authSlice.ts';
import { useAppDispatch } from '../../store/hooks.ts';
import { PatientSummary, setPagination, setSortModel } from '../../store/patientsSlice.ts';
import { RootState } from '../../store/store.ts';
import { PatientStatusChip } from '../ui/DaliaComponents.tsx';

const PatientList = () => {
  const dispatch = useAppDispatch();

  const { patients, loading, pagination, sortModel } = useSelector((state: RootState) => state.patients);
  const { profile } = useSelector((state: RootState) => state.auth);
  const gridRef = useRef<HTMLDivElement>(null);
  const rowHeight = 50;

  const navigate = useNavigate();

  // compute the number of rows that can be displayed in the grid
  useEffect(() => {
    const updatePageSize = () => {
      if (gridRef.current) {
        const gridHeight = gridRef.current.offsetHeight;
        const newPageSize = Math.floor(gridHeight / (rowHeight + 4)) - 2; // remove also the header & footer
        if (pagination.pageSize !== newPageSize) dispatch(setPagination({ page: 0, pageSize: newPageSize }));
      }
    };
    updatePageSize();
    window.addEventListener('resize', updatePageSize);
    return () => {
      window.removeEventListener('resize', updatePageSize);
    };
  }, [dispatch, pagination.pageSize]);

  // refetch the patients when the pagination changes
  useEffect(() => {
    void dispatch(fetchPatients());
  }, [dispatch, pagination, sortModel]);

  const handleSort = (newSortModel: GridSortModel) => {
    const field = newSortModel[0]?.field || sortModel.sortKey;
    if (!field || field === '') return;
    let sort = SortOrder.Asc;
    if (sortModel.sortKey === field && sortModel.sortOrder === SortOrder.Asc) {
      sort = SortOrder.Desc;
    }
    dispatch(setSortModel({ sortKey: field, sortOrder: sort }));
    void dispatch(fetchPatients());
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setPagination(model));
    void dispatch(fetchPatients());
  };

  const handleRowClick = (patient: PatientSummary) => {
    navigate(`/patient/${patient.id}/view/inclusion`);
  };

  const handleDashboardClick = (patient: PatientSummary) => {
    navigate(`/patient/${patient.id}/dashboard/general`);
  };

  const handleDisconnectWatch = (patient: PatientSummary) => {
    void dispatch(disconnectWatch({ id: patient.id }));
  };

  const mainColumns: GridColDef<PatientSummary>[] = [
    { field: 'patientId', headerName: 'Identifiant', sortable: true, flex: 0, align: 'center', headerAlign: 'center' },
    { field: 'externalId', headerName: 'Initiales', sortable: true, flex: 0, align: 'center', headerAlign: 'center' },
    {
      field: 'doctor',
      headerName: 'Médecin',
      sortable: false,
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: BackendUser) => `${params.firstName || ''} ${params.lastName || ''}`,
    },
    {
      field: 'inclusionInfo',
      headerName: "Info d'inclusion",
      sortable: false,
      type: 'boolean',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params != null && params != undefined,
    },

    {
      field: 'createdAt',
      headerName: "Date d'inclusion",
      sortable: true,
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => format(new Date(params), "dd MMM yyyy HH'h'mm"),
    }, //
    {
      field: 'status',
      headerName: 'Statut',
      sortable: false,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <PatientStatusChip patient={params.row} />,
    },
  ];
  const adminColumn: GridColDef<PatientSummary> = {
    field: 'actions',
    headerName: '',
    sortable: false,
    flex: 1,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: any) => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <IconButton
          aria-label="open dashboard"
          className="dashboard-button"
          color="primary"
          onClick={() => {
            handleDashboardClick(params);
          }}
          sx={{ visibility: 'hidden' }}
        >
          <DashboardIcon />
        </IconButton>
        <IconButton
          aria-label="view inclusion"
          className="inclusion-button"
          color="secondary"
          onClick={() => {
            handleRowClick(params);
          }}
          sx={{ visibility: 'hidden' }}
        >
          <InfoIcon />
        </IconButton>
        {params.row.watchType !== 'NONE' && (
          <Tooltip title="Déconnecter la montre">
            <IconButton
              aria-label="disconnect watch"
              className="disconnect-watch-button"
              color="error"
              onClick={() => {
                handleDisconnectWatch(params);
              }}
              sx={{ visibility: 'hidden' }}
            >
              <WatchOffIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  };
  const isAdmin = profile?.role === 'ADMIN';
  const columns = isAdmin ? [...mainColumns, adminColumn] : mainColumns;

  // if the user is not an admin, we add the onRowClick event to redirect to the patient inclusion view
  const extraActionParams = isAdmin
    ? {}
    : {
        onRowClick: (params: { row: PatientSummary }) => {
          handleRowClick(params.row);
        },
      };

  return (
    <Container disableGutters sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography component="h1" gutterBottom sx={{ mt: 4 }} variant="h4">
        Patients
      </Typography>
      {patients?.patients ? (
        <div ref={gridRef} style={{ flex: 1, overflow: 'auto' }}>
          <DataGrid
            columns={columns}
            disableColumnFilter
            loading={loading}
            onPaginationModelChange={(newPaginationModel) => {
              handlePaginationModelChange(newPaginationModel);
            }}
            onSortModelChange={(sortModel) => {
              handleSort(sortModel);
            }}
            // pageSizeOptions={[1, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE * 2]}
            pagination
            paginationMode="server"
            paginationModel={pagination}
            rowCount={patients.total}
            rowHeight={rowHeight} // Set the fixed row height
            rows={patients.patients}
            sortModel={[{ field: sortModel.sortKey, sort: sortModel.sortOrder }]}
            sortingMode="server"
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
                '& .dashboard-button, & .inclusion-button, & .disconnect-watch-button': {
                  visibility: 'visible',
                },
              },
            }}
            {...extraActionParams}
          />
        </div>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="info">Chargement des patients...</Alert>
        </Box>
      )}
    </Container>
  );
};
export default PatientList;
